import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Target, Eye, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { SectionHeading, Swoosh, Txt } from "@/components/site/Brand";
import { ServiceCard, PublicationCard, NewsCard } from "@/components/site/Cards";
import { CtaBand } from "@/components/site/Layout";

function Hero() {
  return (
    <section className="on-dark relative overflow-hidden bg-horizon text-white" data-testid="home-hero">
      <Swoosh className="-right-32 bottom-[-40px] h-[120%] w-[1000px]" />
      <div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-irid-navy/70 to-transparent" aria-hidden="true" />
      <div className="container-irid relative py-24 md:py-32 lg:py-40">
        <span className="eyebrow rise">Research • Innovation • Development</span>
        <h1 className="rise rise-1 mt-6 max-w-4xl text-white">Advancing Knowledge. Strengthening Institutions.</h1>
        <p className="rise rise-2 mt-6 max-w-2xl text-lg text-white/90 md:text-xl">Evidence-based solutions for institutions and businesses.</p>
        <div className="rise rise-3 mt-10 flex flex-wrap gap-4">
          <Link to="/services" className="btn-highlight" data-testid="hero-explore-services">Explore Services <ArrowRight aria-hidden="true" className="h-4 w-4" /></Link>
          <Link to="/contact" className="btn-secondary-dark" data-testid="hero-contact">Contact IRID</Link>
        </div>
      </div>
    </section>
  );
}

function AboutSummary({ s }) {
  const items = [[Target, "Mandate", s?.mandate_intro], [Eye, "Vision", s?.vision], [Compass, "Mission", s?.mission]];
  return (
    <section className="bg-white py-20 md:py-28" data-testid="home-about">
      <div className="container-irid grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeading eyebrow="About IRID" title="A Liberian institution for research and institutional development" />
          <p className="text-irid-ink">{s?.about_intro}</p>
          <Link to="/about" className="btn-secondary mt-8" data-testid="home-about-link">About IRID</Link>
        </div>
        <div className="space-y-5 lg:col-span-7">
          {items.map(([Icon, label, text]) => (
            <div key={label} className="flex gap-5 rounded-xl border border-irid-cloud p-6">
              <Icon aria-hidden="true" className="h-6 w-6 shrink-0 text-irid-blue" />
              <div><h3 className="text-lg">{label}</h3><Txt as="p" className="mt-2">{text}</Txt></div>
            </div>
          ))}
          <div className="flex flex-wrap gap-3 pt-2" aria-label="Values">
            {(s?.values || []).map((v) => (
              <span key={v.name} title={v.description} className="inline-flex items-center gap-2 rounded-full border border-irid-cloud px-3 py-1.5 text-sm font-semibold text-irid-navy"><Leaf aria-hidden="true" className="h-4 w-4 text-irid-green" />{v.name}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { settings, services } = useSite();
  const [pubs, setPubs] = useState([]);
  const [news, setNews] = useState([]);
  useEffect(() => {
    document.title = "IRID – Research. Innovation. Institutional Development.";
    api.get("/publications", { params: { per_page: 3 } }).then((r) => setPubs(r.data.items)).catch(() => {});
    api.get("/news", { params: { per_page: 3 } }).then((r) => setNews(r.data.items)).catch(() => {});
  }, []);
  return (
    <>
      <Hero />
      <AboutSummary s={settings} />
      <section className="bg-irid-mist py-20 md:py-28" data-testid="home-pillars">
        <div className="container-irid">
          <SectionHeading eyebrow="What we do" title="Four strategic service pillars" intro={settings?.services_intro} />
          <div className="grid gap-6 md:grid-cols-2">{services.map((s, i) => <ServiceCard key={s.slug} s={s} index={i} />)}</div>
        </div>
      </section>
      <section className="bg-white py-20 md:py-28" data-testid="home-research">
        <div className="container-irid">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionHeading eyebrow="Research library" title="Featured research and publications" />
            <Link to="/publications" className="btn-secondary mb-10" data-testid="home-research-all">Browse the library</Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{pubs.map((p, i) => <PublicationCard key={p.slug} pub={p} index={i} />)}</div>
        </div>
      </section>
      <section className="bg-irid-mist py-20 md:py-28" data-testid="home-news">
        <div className="container-irid">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionHeading eyebrow="News and insights" title="Latest from IRID" />
            <Link to="/news" className="btn-secondary mb-10" data-testid="home-news-all">All news</Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{news.map((n, i) => <NewsCard key={n.slug} n={n} index={i} />)}</div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
